import React, { useEffect, useState } from 'react';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, onSnapshot, doc, deleteDoc, updateDoc, getDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import {
  Typography, TextField, Button, Card, CardContent, List, Box, ListItem, ListItemText, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import styles from '../styles/Dashboard.module.css';

const GerenciarAreas = () => {
  const [areas, setAreas] = useState([]);
  const [novaArea, setNovaArea] = useState({ nome: '', descricao: '', capacidade_maxima: '', regras: '', custo_reserva: '' });
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');

  // State for editing dialog
  const [editDialog, setEditDialog] = useState(false);
  const [areaSelecionada, setAreaSelecionada] = useState(null);
  const [editFormData, setEditFormData] = useState({ nome: '', descricao: '', capacidade_maxima: '', regras: '', custo_reserva: '' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!condominioId) return;
    const q = query(collection(db, 'tb_areas_comuns'), where('condominio_id', '==', condominioId), orderBy('created_at', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const areasList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAreas(areasList);
    });
    return () => unsubscribe();
  }, [condominioId]);

  const handleCreateArea = async () => {
    if (!novaArea.nome) {
      alert('O nome da área é obrigatório.');
      return;
    }
    try {
      await addDoc(collection(db, 'tb_areas_comuns'), {
        ...novaArea,
        capacidade_maxima: parseInt(novaArea.capacidade_maxima) || 0,
        custo_reserva: parseFloat(novaArea.custo_reserva) || 0,
        condominio_id: condominioId,
        created_at: serverTimestamp()
      });
      setNovaArea({ nome: '', descricao: '', capacidade_maxima: '', regras: '', custo_reserva: '' });
      alert('Área comum adicionada com sucesso!');
    } catch (error) {
      console.error("Erro ao criar área:", error);
      alert('Erro ao criar área: ' + error.message);
    }
  };

  const handleDeleteArea = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar esta área? Todas as reservas futuras para esta área também serão perdidas.')) {
      try {
        await deleteDoc(doc(db, 'tb_areas_comuns', id));
        alert('Área deletada com sucesso.');
      } catch (error) {
        console.error("Erro ao deletar área:", error);
        alert('Erro ao deletar área: ' + error.message);
      }
    }
  };

  const openEditDialog = (area) => {
    setAreaSelecionada(area);
    setEditFormData({
      nome: area.nome,
      descricao: area.descricao || '',
      capacidade_maxima: area.capacidade_maxima || '',
      regras: area.regras || '',
      custo_reserva: area.custo_reserva || ''
    });
    setEditDialog(true);
  };

  const handleUpdateArea = async () => {
    if (!editFormData.nome) {
      alert('O nome da área é obrigatório.');
      return;
    }
    try {
      const areaRef = doc(db, 'tb_areas_comuns', areaSelecionada.id);
      await updateDoc(areaRef, {
        ...editFormData,
        capacidade_maxima: parseInt(editFormData.capacidade_maxima) || 0,
        custo_reserva: parseFloat(editFormData.custo_reserva) || 0,
      });
      setEditDialog(false);
      setAreaSelecionada(null);
      alert('Área atualizada com sucesso!');
    } catch (error) {
      console.error("Erro ao atualizar área:", error);
      alert('Erro ao atualizar área: ' + error.message);
    }
  };

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro', 'zelador'].includes(userRole);

  if (!isAdmin) {
    return (
      <Box>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Typography variant="h4">Acesso Negado</Typography>
          <Typography>Você não tem permissão para acessar esta página.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Gerenciar Áreas Comuns</Typography>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Adicionar Nova Área</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Nome da Área (Ex: Salão de Festas)" value={novaArea.nome} onChange={(e) => setNovaArea({ ...novaArea, nome: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="number" label="Capacidade Máxima" value={novaArea.capacidade_maxima} onChange={(e) => setNovaArea({ ...novaArea, capacidade_maxima: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="number" label="Custo da Reserva (R$)" value={novaArea.custo_reserva} onChange={(e) => setNovaArea({ ...novaArea, custo_reserva: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Descrição (opcional)" value={novaArea.descricao} onChange={(e) => setNovaArea({ ...novaArea, descricao: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Regras de Uso (opcional)" value={novaArea.regras} onChange={(e) => setNovaArea({ ...novaArea, regras: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleCreateArea} size="large">Adicionar Área</Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Áreas Cadastradas</Typography>
            <List>
              {areas.map((area) => (
                <ListItem key={area.id} divider secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" onClick={() => openEditDialog(area)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteArea(area.id)} sx={{ ml: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }>
                  <ListItemText
                    primary={area.nome}
                    secondary={`Capacidade: ${area.capacidade_maxima || 'N/A'} | Custo: R$ ${area.custo_reserva?.toFixed(2) || '0.00'}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Editar Área Comum</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nome da Área" type="text" fullWidth variant="standard" value={editFormData.nome} onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })} />
          <TextField margin="dense" label="Descrição" type="text" fullWidth variant="standard" value={editFormData.descricao} onChange={(e) => setEditFormData({ ...editFormData, descricao: e.target.value })} />
          <TextField margin="dense" label="Capacidade Máxima" type="number" fullWidth variant="standard" value={editFormData.capacidade_maxima} onChange={(e) => setEditFormData({ ...editFormData, capacidade_maxima: e.target.value })} />
          <TextField margin="dense" label="Custo da Reserva (R$)" type="number" fullWidth variant="standard" value={editFormData.custo_reserva} onChange={(e) => setEditFormData({ ...editFormData, custo_reserva: e.target.value })} />
          <TextField margin="dense" label="Regras de Uso" type="text" multiline rows={3} fullWidth variant="standard" value={editFormData.regras} onChange={(e) => setEditFormData({ ...editFormData, regras: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button onClick={handleUpdateArea}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerenciarAreas;
